import { betriebZuIntern } from "../../lib/bereich";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { date, money, time } from "../../lib/format";
import { Fehler, Fortschritt, Laden } from "./parts";
import { TrafficDashboard } from "./Traffic";
import { currentLang, t } from "../../lib/sprache";
import { useAccount } from "../../app/session";
import { gruss } from "./traffic-model";

/**
 * Übersicht der Betreiber-Konsole (0213, Inhaber 13.10.2026): Kacheln, neue Konten je Tag, der Weg vom Konto zum zahlenden Kunden,
 * „Braucht Aufmerksamkeit“ (dieselbe Liste wie im Morgenbericht), letzte Ereignisse und neueste Konten.
 * Eigene und Testkonten zählen nur mit, wenn der Schalter gesetzt ist.
 * 0330: oben das Traffic-Dashboard (Traffic.tsx) mit demselben Zeitraum (höchstens 90 Tage) und demselben Schalter, der dort
 * zusätzlich die eigenen Zugriffe aus PT, US und ES einblendet; daneben die Wahl der Startseite nach dem Login.
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
type Daten = {
  tage: number; intern: boolean; ausgeblendet: number; tickets_offen: number;
  kacheln: { konten: number; neu_7: number; neu_zeitraum: number; testphase: number; testphase_zahlungsart: number; zahlend: number; mrr_cents: number; kuendigungen: number; gesperrt: number };
  verlauf: { tag: string; neu: number }[];
  trichter: { schritt: string; n: number }[];
  aufmerksamkeit: string[];
  ereignisse: { at: string; titel: string; grund: string | null; pfad: string | null }[];
  neueste: { id: string; name: string; kind: string; status: string; tarif: string | null; created_at: string; trial_ends_at: string | null; zahlungsart: boolean; zuletzt: string | null; intern: boolean; schritt: string; herkunft?: string | null }[];
};
const STATUS: Record<string, string> = { trial: "Testphase", active: "Aktiv", past_due: "Zahlung offen", suspended: "Gesperrt", cancelled: "Gekündigt", readonly: "Nur lesen" };
const ZEITRAUM = [7, 30, 90, 365];

function Verlauf({ werte }: { werte: Daten["verlauf"] }) {
  const hoch = Math.max(0, ...werte.map((w) => w.neu));
  const max = Math.max(1, hoch);
  const b = 100 / werte.length;
  const summe = werte.reduce((s, w) => s + w.neu, 0);
  return (
    <figure className="tk-betrieb-verlauf">
      <svg viewBox="0 0 100 32" preserveAspectRatio="none" role="img" aria-label={`Neue Konten je Tag, zusammen ${summe}, höchster Tageswert ${hoch}`}>
        {werte.map((w, i) => {
          const h = (w.neu / max) * 30;
          return <rect key={w.tag} x={i * b + b * 0.15} y={32 - Math.max(h, 0.4)} width={b * 0.7} height={Math.max(h, 0.4)} className={w.neu ? "voll" : "leer"}><title>{`${date(w.tag)}: ${w.neu}`}</title></rect>;
        })}
      </svg>
      <figcaption><span>{date(werte[0]!.tag)}</span><span>höchster Tag: {hoch}</span><span>{date(werte[werte.length - 1]!.tag)}</span></figcaption>
    </figure>
  );
}

/**
 * Testphasen (Inhaber 24.10.2026): wer nach dem Testende automatisch zahlt (Zahlungsart hinterlegt) und wer ausläuft, nach
 * Testende sortiert. Quelle ist platform_trials (0443): alle laufenden Testphasen, eigene und Testkonten nur mit „Intern zeigen“.
 */
export type Testphase = { id: string; name: string; tarif: string | null; trial_ends_at: string | null; zahlungsart: boolean; intern: boolean };
export function testphasenListe(konten: Testphase[], heute: Date): { id: string; name: string; tarif: string | null; ende: string; tage: number; zahlt: boolean }[] {
  const tag0 = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate());
  return konten
    .filter((k) => !!k.trial_ends_at)
    .map((k) => {
      const e = new Date(k.trial_ends_at!);
      return { id: k.id, name: k.name, tarif: k.tarif, ende: k.trial_ends_at!, zahlt: k.zahlungsart,
        tage: Math.round((Date.UTC(e.getFullYear(), e.getMonth(), e.getDate()) - tag0) / 86_400_000) };
    })
    .sort((a, b) => a.ende.localeCompare(b.ende));
}

/** Abstand zum Testende in Worten; K-6: schon vorbei, aber noch „trial“ (vor dem nächtlichen Lauf) → abgelaufen */
export function testendeText(n: number, t: (de: string, en: string) => string): string {
  if (n < -1) return t(`seit ${-n} Tagen abgelaufen`, `expired ${-n} days ago`);
  if (n === -1) return t("seit gestern abgelaufen", "expired yesterday");
  if (n === 0) return t("heute", "today");
  return n === 1 ? t("morgen", "tomorrow") : t(`in ${n} Tagen`, `in ${n} days`);
}

function Testphasen({ intern }: { intern: boolean }) {
  const q = useQuery({ queryKey: ["platform_trials", intern], refetchInterval: 5 * 60_000,
    queryFn: async () => parse<Testphase[]>(await api.read("platform_trials", { p_internal: intern })) });
  const liste = q.data ? testphasenListe(q.data, new Date()) : [];
  return (
    <section className="panel stack" aria-labelledby="test-h">
      <h2 id="test-h">{t("Testphasen", "Trials")}</h2>
      <small className="muted">{t("Wer nach dem Testende automatisch zahlt und wer ausläuft", "Who pays automatically after the trial and who lapses")}</small>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !q.data ? <Laden /> : liste.length === 0 ? <p>{t("Gerade keine laufende Testphase.", "No running trial right now.")}</p> : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">{t("Laufende Testphasen nach Testende", "Running trials by end date")}</caption>
            <thead><tr><th scope="col">{t("Konto", "Account")}</th><th scope="col">{t("Testende", "Trial ends")}</th><th scope="col">{t("Danach", "Then")}</th></tr></thead>
            <tbody>
              {liste.map((k) => (
                <tr key={k.id}>
                  <td><Link to={`/intern/agenturen/${k.id}`}>{k.name}</Link>{k.tarif ? <small> · {k.tarif}</small> : null}</td>
                  <td>{date(k.ende)} <small className="muted">{testendeText(k.tage, t)}</small></td>
                  <td>{k.zahlt ? <span className="pill success">{t("zahlt automatisch", "pays automatically")}</span> : <span className="pill warning">{t("läuft aus", "lapses")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function Uebersicht({ vertrieb }: { vertrieb?: ReactNode } = {}) {
  const [tage, setTage] = useState(30);
  const [intern, setIntern] = useState(false);
  const q = useQuery({ queryKey: ["platform_dashboard", tage, intern], refetchInterval: 5 * 60_000,
    queryFn: async () => parse<Daten>(await api.read("platform_dashboard", { p_days: tage, p_internal: intern })) });
  const { account } = useAccount();
  // Inhaber 24.10.2026 (Konsole als eigene App): Gruß mit Vornamen und heutigem Datum statt „Übersicht“
  const vorname = account?.platformName?.trim().split(/\s+/)[0] ?? account?.memberships.map((m) => m.display_name?.trim().split(/\s+/)[0]).find(Boolean) ?? null;
  const jetzt = new Date();
  const kopf = <><title>Übersicht · Plattform · CreatorOffice</title>
    {/* Vertrieb-Konsole: Datum neben statt in der Überschrift (der Tab-Titel nimmt die erste h1 im Portal-Rahmen) */}
    <div className="tk-konsole-gruss"><h1>{gruss(jetzt.getHours(), t)}{vorname ? `, ${vorname}` : ""}</h1>
      <small>{jetzt.toLocaleDateString(currentLang() === "en" ? "en-GB" : "de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</small></div></>;
  if (q.error) return <>{kopf}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <>{kopf}<Laden /></>;
  const d = q.data, k = d.kacheln, start = d.trichter[0]?.n ?? 0;

  return (
    <>
      {kopf}
      <div className="row tk-betrieb-filter" style={{ gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label>Zeitraum{" "}
          <select value={tage} onChange={(e) => setTage(Number(e.target.value))}>{ZEITRAUM.map((t) => <option key={t} value={t}>{t === 365 ? "12 Monate" : `${t} Tage`}</option>)}</select>
        </label>
        <label className="row" style={{ gap: 6, alignItems: "center" }}><input type="checkbox" checked={intern} onChange={(e) => setIntern(e.target.checked)} /> {t("Intern zeigen", "Show internal")}</label>
        <small>{intern ? t("mit eigenen und Testkonten und Zugriffen aus PT, US, ES", "including own and test accounts and visits from PT, US, ES")
          : `${d.ausgeblendet > 0 ? t(`${d.ausgeblendet} eigene oder Testkonten ausgeblendet`, `${d.ausgeblendet} own or test accounts hidden`) : t("ohne eigene und Testkonten", "without own and test accounts")}${t(", ohne Zugriffe aus PT, US, ES", ", without visits from PT, US, ES")}`}</small>
      </div>

      <TrafficDashboard tage={tage} intern={intern} />

      <div className="grid two">
        <Testphasen intern={intern} />
        <section className="panel stack" aria-labelledby="auf-h">
          <h2 id="auf-h">Braucht Aufmerksamkeit</h2>
          {d.aufmerksamkeit.length === 0 ? <p>Gerade nichts. Dieselbe Liste steht jeden Morgen um 8 Uhr im Morgenbericht.</p>
            : <ul className="tk-betrieb-liste">{d.aufmerksamkeit.map((z, i) => <li key={i}>{z.replace(/^•\s*/, "")}</li>)}</ul>}
        </section>
      </div>

      {/* Vertrieb-Konsole (Inhaber 02.11.2026): Vertriebsstand für den Admin (Stufen, Rabatt-Vorschläge, fällige Aufgaben) */}
      {vertrieb}

      <h2 className="tk-abschnitt">{t("Konten und Betrieb", "Accounts and operations")}</h2>

      <div className="grid three">
        <section className="panel stack"><h2>Konten</h2><div className="metric">{k.konten}</div><p>{k.neu_7} neu in 7 Tagen · {k.neu_zeitraum} im Zeitraum</p></section>
        <section className="panel stack"><h2>Testphasen</h2><div className="metric">{k.testphase}</div><p>{k.testphase_zahlungsart} mit Zahlungsart hinterlegt</p></section>
        <section className="panel stack"><h2>Zahlende Kunden</h2><div className="metric">{k.zahlend}</div><p>{k.kuendigungen} {k.kuendigungen === 1 ? "Kündigung" : "Kündigungen"} vorgemerkt · {k.gesperrt} gesperrt</p></section>
        <section className="panel stack"><h2>Monatsumsatz · netto</h2><div className="metric num">{money(k.mrr_cents)}</div><p>laut Tarif, Jahresabos anteilig</p></section>
        <section className="panel stack"><h2>Jahresumsatz · netto</h2><div className="metric num">{money(k.mrr_cents * 12)}</div><p>Monatsumsatz mal zwölf</p></section>
        <section className="panel stack"><h2>Offene Tickets</h2><div className="metric">{d.tickets_offen}</div><p><Link to="/intern/tickets">Zu den Tickets</Link></p></section>
      </div>

      <section className="panel stack" aria-labelledby="verlauf-h">
        <h2 id="verlauf-h">Neue Konten je Tag</h2>
        <Verlauf werte={d.verlauf} />
      </section>

      <div className="grid two">
        <section className="panel stack" aria-labelledby="weg-h">
          <h2 id="weg-h">Vom Konto zum zahlenden Kunden</h2>
          {start === 0 ? <p>Noch keine Konten.</p> : d.trichter.map((s) => (
            <div key={s.schritt} className="stack" style={{ gap: 4 }}>
              <div className="row" style={{ justifyContent: "space-between", gap: 8 }}><span>{s.schritt}</span><strong>{s.n} · {Math.round((s.n / start) * 100)} %</strong></div>
              <Fortschritt pct={(s.n / start) * 100} label={`${s.schritt}: ${s.n} von ${start}`} />
            </div>
          ))}
        </section>
        <CocoRundgangZahlen tage={tage} intern={intern} />
      </div>

      <section className="stack" aria-labelledby="neu-h">
        <h2 id="neu-h">Neueste Konten</h2>
        {d.neueste.length === 0 ? <div className="panel tk-empty"><p>Noch keine Konten.</p></div> : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Neueste Konten</caption>
              <thead><tr><th scope="col">Konto</th><th scope="col">Angelegt</th><th scope="col">Status</th><th scope="col">Tarif</th><th scope="col">Stand</th><th scope="col">Herkunft</th><th scope="col">Zuletzt im Portal</th></tr></thead>
              <tbody>
                {d.neueste.map((n) => (
                  <tr key={n.id}>
                    <td><Link to={`/intern/agenturen/${n.id}`}>{n.name}</Link><br /><small>{n.kind === "creator" ? "Creator-Konto" : "Agentur"}{n.intern ? " · eigenes oder Testkonto" : ""}</small></td>
                    <td>{date(n.created_at)}</td>
                    <td>{STATUS[n.status] ?? n.status}{n.status === "trial" && n.trial_ends_at ? <><br /><small>bis {date(n.trial_ends_at)}</small></> : null}</td>
                    <td>{n.tarif ?? "–"}</td>
                    <td>{n.schritt}</td>
                    <td>{n.herkunft ?? "–"}</td>
                    <td>{n.zuletzt ? `${date(n.zuletzt)} ${time(n.zuletzt)}` : "noch nie"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p><Link to="/intern/agenturen">Alle Konten mit Filter</Link></p>
      </section>

      <section className="stack" aria-labelledby="erg-h">
        <h2 id="erg-h">Letzte Ereignisse</h2>
        {d.ereignisse.length === 0 ? <div className="panel tk-empty"><p>Noch keine Ereignisse. Neue Konten, Zahlungsarten, Zahlungen und Kündigungen erscheinen hier und kommen sofort per Mail. Eigene und Testkonten lösen keine Ereignisse aus.</p></div> : (
          <ul className="tk-betrieb-liste panel">
            {d.ereignisse.map((e, i) => (
              <li key={i}><strong>{e.pfad && betriebZuIntern(e.pfad) !== "/intern" ? <Link to={betriebZuIntern(e.pfad)}>{e.titel}</Link> : e.titel}</strong><br /><small>{date(e.at)} {time(e.at)}{e.grund ? ` · ${e.grund}` : ""}</small></li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/**
 * Rundgang mit Coco (0380): gestartet, beendet, übersprungen je Rolle (Personen), wo übersprungen wird, und die Zeit vom
 * Anlegen der Agentur bis zur ersten eigenen Kooperation, getrennt nach dem Rundgang des Admins. Nur Zählwerte.
 */
type CocoZahlen = {
  rollen: { rolle: "admin" | "team" | "creator"; gestartet: number; beendet: number; uebersprungen: number; schritte: { schritt: number; n: number }[] }[];
  erste_kooperation: { agenturen: number; mit_kooperation: number; median_stunden: number | null;
    gruppen: { gruppe: "beendet" | "uebersprungen" | "abgebrochen" | "ohne"; agenturen: number; mit_kooperation: number; median_stunden: number | null }[] };
};
const ROLLE: Record<string, string> = { admin: "Agentur-Admin", team: "Team", creator: "Creator-Portal", studio: "Creator-Konto" };
const GRUPPE: Record<string, string> = { beendet: "Rundgang beendet", uebersprungen: "übersprungen", abgebrochen: "abgebrochen", ohne: "ohne Rundgang" };
/** Stunden lesbar: unter zwei Tagen in Stunden, sonst in Tagen */
export function dauerText(stunden: number | null): string {
  if (stunden === null) return "–";
  if (stunden < 48) return `${String(Math.round(stunden * 10) / 10).replace(".", ",")} Std.`;
  return `${String(Math.round((stunden / 24) * 10) / 10).replace(".", ",")} Tage`;
}

function CocoRundgangZahlen({ tage, intern }: { tage: number; intern: boolean }) {
  const q = useQuery({ queryKey: ["platform_coco_tour_stats", tage, intern], refetchInterval: 5 * 60_000,
    queryFn: async () => parse<CocoZahlen>(await api.read("platform_coco_tour_stats", { p_days: tage, p_internal: intern })) });
  return (
    <section className="panel stack" aria-labelledby="coco-h">
      <h2 id="coco-h">Rundgang mit Coco</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !q.data ? <Laden /> : (
        <>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Rundgang je Rolle, Personen im Zeitraum</caption>
              <thead><tr><th scope="col">Rolle</th><th scope="col" className="num">gestartet</th><th scope="col" className="num">beendet</th><th scope="col" className="num">übersprungen</th><th scope="col">übersprungen bei Schritt</th></tr></thead>
              <tbody>
                {q.data.rollen.map((r) => (
                  <tr key={r.rolle}>
                    <td>{ROLLE[r.rolle] ?? r.rolle}</td>
                    <td className="num">{r.gestartet}</td>
                    <td className="num">{r.beendet}{r.gestartet ? <small> · {Math.round((r.beendet / r.gestartet) * 100)} %</small> : null}</td>
                    <td className="num">{r.uebersprungen}</td>
                    <td>{r.schritte.length ? r.schritte.map((x) => `${x.schritt}: ${x.n}`).join(" · ") : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3>Bis zur ersten Kooperation</h3>
          <p>{q.data.erste_kooperation.agenturen === 0 ? "Noch keine neuen Agenturen im Zeitraum."
            : `${q.data.erste_kooperation.mit_kooperation} von ${q.data.erste_kooperation.agenturen} neuen Agenturen im Zeitraum haben eine eigene Kooperation angelegt (ohne Beispieldaten)${q.data.erste_kooperation.median_stunden !== null ? `, im Mittel (Median) nach ${dauerText(q.data.erste_kooperation.median_stunden)}` : ""}.`.replace(/\.\.$/, ".")}</p>
          <ul className="tk-betrieb-liste">
            {q.data.erste_kooperation.gruppen.filter((g) => g.agenturen > 0).map((g) => (
              <li key={g.gruppe}>{GRUPPE[g.gruppe] ?? g.gruppe}: {g.mit_kooperation} von {g.agenturen} · Median {dauerText(g.median_stunden)}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
